import {
  mdiAccountCircleOutline,
  mdiHelpCircleOutline,
  mdiLogoutVariant,
  mdiMenu,
  mdiOfficeBuildingOutline
} from '@mdi/js';
import Icon from '@mdi/react';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Typography
} from '@mui/material';
import MaterialAppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Logo from '../../../assets/logo.png';
import { useUserContext } from '../../../contexts/User.context';
import { TipoUsuario } from '../../../models/Usuario';
import { Routes } from '../../../routes/index.routes';
import { Colors } from '../../../shared/colors';
import { useAuthenticationContext } from '../../features/authentication/authenticationContext';
import Link from '../Link/Link';
import { usePjGuidedTour } from '../PjGuidedTour';

import { ResponsiveMenu } from './ResponsiveMenu';

const ajudaSx = {
  height: 36,
  borderRadius: '18px',
  border: '1px solid rgba(255, 255, 255, 0.55)',
  bgcolor: 'rgba(255, 255, 255, 0.14)',
  color: Colors.white,
  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.24)' }
};

const placeholderSx = { bgcolor: 'rgba(255, 255, 255, 0.24)' };

function AppBar() {
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuthenticationContext();
  const { loading, user, userInfo, userClaim } = useUserContext();
  const { startTour: startPjTour } = usePjGuidedTour();

  const usuarioPJ = userClaim === TipoUsuario.PESSOA_JURIDICA;
  const podeVerTour = Boolean(
    user &&
      ((usuarioPJ && 'cnpj' in user) || (userClaim === TipoUsuario.PESSOA_FISICA && 'rg' in user))
  );
  const nomeExibicao = user ? ('cnpj' in user ? user.nomeFantasia : user.nome) : userInfo?.nome;

  const irParaHome = () => navigate(Routes.Home);
  const iniciarTourCarteira = () => navigate(Routes.Home, { state: { startWalletTour: true } });
  const iniciarTour = () => {
    if (usuarioPJ) startPjTour();
    else iniciarTourCarteira();
  };
  const irParaConta = () => navigate(Routes.Conta.Principal);
  const sair = useCallback(() => logout(), [logout]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <MaterialAppBar position="static" sx={{ zIndex: { xs: 1201 } }}>
        <Toolbar disableGutters sx={{ height: { xs: 60, sm: 80 } }}>
          <Container maxWidth="lg">
            <Box flex={1} display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                {userInfo && (
                  <IconButton
                    data-tour-pj="menu"
                    aria-label="Abrir menu"
                    aria-expanded={open}
                    onClick={() => setOpen((prev) => !prev)}
                    sx={{
                      display: usuarioPJ
                        ? { xs: 'inline-flex', lg: 'none' }
                        : { xs: 'inline-flex', md: 'none' },
                      width: 44,
                      height: 44,
                      ml: -1,
                      color: 'inherit'
                    }}
                  >
                    <Icon path={mdiMenu} size="28px" />
                  </IconButton>
                )}

                <ButtonBase
                  aria-label="Ir para a Carteira"
                  onClick={irParaHome}
                  sx={{ borderRadius: 1 }}
                >
                  <Box
                    component="img"
                    src={Logo}
                    alt="Águia Branca"
                    sx={{ display: 'block', width: { xs: 170, sm: 220 }, height: 'auto' }}
                  />
                </ButtonBase>
              </Box>

              {userInfo && loading && (
                <Box
                  aria-hidden="true"
                  display={{ xs: 'none', md: 'flex' }}
                  gap={3}
                  alignItems="center"
                >
                  <Skeleton variant="rounded" width={150} height={32} sx={placeholderSx} />
                  <Skeleton variant="rounded" width={90} height={36} sx={placeholderSx} />
                  <Skeleton variant="rounded" width={56} height={20} sx={placeholderSx} />
                </Box>
              )}

              {userInfo && !loading && (
                <Box display={{ xs: 'none', md: 'flex' }} gap={3} alignItems="center" height="100%">
                  <ButtonBase
                    data-tour-pj="account"
                    onClick={irParaConta}
                    sx={{ gap: 1.25, borderRadius: 1, textAlign: 'left' }}
                  >
                    <Icon
                      path={usuarioPJ ? mdiOfficeBuildingOutline : mdiAccountCircleOutline}
                      size={1.25}
                    />

                    <Box display="flex" flexDirection="column">
                      <Typography fontSize={14} fontWeight={600} lineHeight={1.2}>
                        {nomeExibicao}
                      </Typography>
                      <Typography fontSize={12} fontWeight={400} lineHeight={1.2}>
                        {usuarioPJ ? 'Pessoa jurídica' : 'Pessoa física'}
                      </Typography>
                    </Box>
                  </ButtonBase>

                  {podeVerTour && (
                    <Button
                      onClick={iniciarTour}
                      startIcon={<Icon path={mdiHelpCircleOutline} size={0.85} />}
                      sx={{
                        ...ajudaSx,
                        px: 1.75,
                        fontSize: 14,
                        fontWeight: 500,
                        textTransform: 'none',
                        '& .MuiButton-startIcon': { mr: 0.75 }
                      }}
                    >
                      Ajuda
                    </Button>
                  )}

                  <Divider
                    orientation="vertical"
                    sx={{ borderColor: 'rgba(255, 255, 255, 0.45)', height: 24 }}
                  />

                  <ButtonBase onClick={sair} sx={{ gap: 1, borderRadius: 1, py: 0.5 }}>
                    <Icon path={mdiLogoutVariant} size={0.8} />
                    <Typography fontSize={14} fontWeight={400}>
                      Sair
                    </Typography>
                  </ButtonBase>
                </Box>
              )}
            </Box>
          </Container>
        </Toolbar>

        {usuarioPJ && (
          <Toolbar
            disableGutters
            sx={{
              bgcolor: 'primary.dark',
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'flex-end',
              minHeight: { xs: 56 }
            }}
          >
            <Container maxWidth="lg">
              <Box
                display="flex"
                alignItems="flex-end"
                justifyContent="space-between"
                width={1}
                sx={{ '& > a': { flex: 1, textAlign: 'center' } }}
              >
                <Link to={Routes.Home} end>
                  Carteira
                </Link>
                <Link to={Routes.Usuarios.Principal}>Gestão de usuários</Link>
                <Link to={Routes.Transacoes.Principal}>Transações de usuários</Link>
                <Link to={Routes.Extrato.Principal}>Consumo pós-pago</Link>
              </Box>
            </Container>
          </Toolbar>
        )}
      </MaterialAppBar>

      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        sx={{ zIndex: { xs: 1300 } }}
        PaperProps={{
          sx: {
            bgcolor: Colors.white,
            maxWidth: '100%',
            width: { xs: '86%', sm: 380 }
          }
        }}
      >
        <ResponsiveMenu
          onNavigate={() => setOpen(false)}
          usuarioPJ={usuarioPJ}
          nome={nomeExibicao}
          onAjuda={
            podeVerTour
              ? () => {
                  setOpen(false);
                  iniciarTour();
                }
              : undefined
          }
          onSair={sair}
        />
      </Drawer>
    </>
  );
}
export default AppBar;
